/**
 * LiveStationPicker — assign a live-wind Worker station to a spot (SRF-2855).
 *
 * A CORRECTION tool, not a from-zero entry form: most spots already carry an assignment, so
 * the panel leads with what the current id resolves to (reading, or "not found on live-wind"
 * for the bare legacy ids), then offers the nearest candidates, and validates any typed id
 * against the Worker before the parent may save it.
 *
 * Talks to the Worker directly (CORS-allowlisted origin). Saving goes through the parent's
 * editspot call with `liveWindStationId` only — name and distance are never stored, the
 * Worker is the single source of truth for them.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { LiveWindStationService, LiveWindStation, StationLookup, isValidStationId } from "@/services/LiveWindStationService";

interface Props {
  spotLat: number;
  spotLng: number;
  /** The id as currently edited ("" = none / clear). */
  value: string;
  /** The id as stored on the spot when the modal opened. */
  originalValue: string;
  onChange: (id: string) => void;
  /**
   * true = saveable, false = must not save, null = not yet known. `validatedId` is the exact
   * id the verdict is about — the parent must refuse to save a different one.
   */
  onValidityChange: (valid: boolean | null, reason?: string, validatedId?: string) => void;
}

const fmtReading = (s: LiveWindStation): string => {
  if (s.windKts == null) return "no reading";
  const gust = s.gustKts != null && s.gustKts !== s.windKts ? `/${Math.round(s.gustKts)}` : "";
  const dir = s.windDir != null ? ` · ${Math.round(s.windDir)}°` : "";
  return `${Math.round(s.windKts)}${gust} kt${dir}${s.stale ? " · STALE" : ""}`;
};
const fmtAge = (obsTs: number | null): string => {
  if (obsTs == null) return "";
  const min = Math.max(0, Math.round((Date.now() - obsTs) / 60000));
  return min < 1 ? "just now" : `${min} min ago`;
};

const box: React.CSSProperties = {
  backgroundColor: "#111827",
  borderRadius: "8px",
  border: "1px solid #374151",
  padding: "12px",
};
const mono: React.CSSProperties = { fontFamily: "monospace" };

export function LiveStationPicker({ spotLat, spotLng, value, originalValue, onChange, onValidityChange }: Props) {
  const [lookup, setLookup] = useState<StationLookup | null>(null);
  const [checking, setChecking] = useState(false);
  const [candidates, setCandidates] = useState<LiveWindStation[] | null | undefined>(undefined);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const seq = useRef(0);

  // Validate whatever id is in the box (debounced on typing, immediate on blur/selection).
  const validate = useCallback(
    async (id: string) => {
      const mine = ++seq.current;
      const trimmed = id.trim();
      if (!trimmed) {
        setChecking(false);
        setLookup(null);
        onValidityChange(true, undefined, ""); // clearing is always saveable
        return;
      }
      if (!isValidStationId(trimmed)) {
        setChecking(false);
        setLookup({ status: "invalid_id" });
        onValidityChange(false, "Not a live-wind station id (expected <provider>_<id>, max 64 chars)", trimmed);
        return;
      }
      setChecking(true);
      onValidityChange(null, undefined, trimmed);
      const result = await LiveWindStationService.lookup(trimmed);
      if (mine !== seq.current) return; // superseded — a newer value owns the verdict
      setChecking(false);
      setLookup(result);
      if (result.status === "found") onValidityChange(true, undefined, trimmed);
      else if (result.status === "not_found") onValidityChange(false, "That id is not known to the live-wind service", trimmed);
      else if (result.status === "invalid_id") onValidityChange(false, "The live-wind service rejected that id", trimmed);
      else onValidityChange(false, result.message, trimmed);
    },
    [onValidityChange]
  );

  // Any change of the value invalidates the previous verdict IMMEDIATELY (and retires any
  // in-flight lookup), then re-validates after a short debounce. Without this, selecting B
  // right after A validated could save B unverified, or A's late answer could bless B.
  useEffect(() => {
    seq.current += 1;
    const trimmed = value.trim();
    if (!trimmed) {
      setChecking(false);
      setLookup(null);
      onValidityChange(true, undefined, "");
      return undefined;
    }
    setChecking(true);
    onValidityChange(null, undefined, trimmed);
    const t = setTimeout(() => validate(value), value === originalValue ? 0 : 400);
    return () => clearTimeout(t);
  }, [value, originalValue, validate, onValidityChange]);

  // Candidates describe a position: moving the spot discards them and offers a reload.
  useEffect(() => {
    setCandidates(undefined);
  }, [spotLat, spotLng]);

  const loadCandidates = useCallback(async () => {
    setLoadingCandidates(true);
    const list = await LiveWindStationService.nearest(spotLat, spotLng, 10);
    setCandidates(list);
    setLoadingCandidates(false);
  }, [spotLat, spotLng]);

  const current = lookup?.status === "found" ? lookup.station : null;

  return (
    <div>
      <label style={{ display: "block", color: "#9ca3af", fontSize: "13px", marginBottom: "6px", fontWeight: 500 }}>
        Live Wind Station
      </label>

      {/* Current assignment */}
      <div style={{ ...box, marginBottom: "10px" }}>
        {!value.trim() ? (
          <span style={{ color: "#9ca3af", fontSize: "12px" }}>No station assigned — the app shows forecast only.</span>
        ) : checking ? (
          <span style={{ color: "#9ca3af", fontSize: "12px" }}>Checking {value.trim()} on the live-wind service…</span>
        ) : current ? (
          <div style={{ fontSize: "12px", color: "#d1d5db" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
              <span style={{ color: "#f9fafb", fontWeight: 600 }}>{current.name}</span>
              <span style={{ color: current.stale ? "#fbbf24" : "#34d399" }}>{fmtReading(current)}</span>
            </div>
            <div style={{ color: "#9ca3af", marginTop: "2px" }}>
              <span style={mono}>{current.id}</span> · {current.source}
              {current.obsTs != null ? ` · ${fmtAge(current.obsTs)}` : ""}
              {current.attribution ? ` · powered by ${current.attribution.label}` : ""}
            </div>
          </div>
        ) : lookup?.status === "not_found" ? (
          <span style={{ color: "#f87171", fontSize: "12px" }}>
            <span style={mono}>{value.trim()}</span> is not known to the live-wind service — pick a candidate below or clear it.
          </span>
        ) : lookup?.status === "invalid_id" ? (
          <span style={{ color: "#f87171", fontSize: "12px" }}>Not a live-wind station id (expected &lt;provider&gt;_&lt;id&gt;).</span>
        ) : lookup?.status === "unreachable" ? (
          <span style={{ color: "#fbbf24", fontSize: "12px" }}>{lookup.message} — cannot validate, saving is disabled.</span>
        ) : null}
      </div>

      {/* Free-text id */}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => validate(value)}
        placeholder="e.g. knmi_6225 or clientraw_br-cumbuco-ocen-view — blank clears"
        spellCheck={false}
        style={{
          width: "100%",
          padding: "12px",
          borderRadius: "8px",
          border: `1px solid ${lookup && lookup.status !== "found" && value.trim() ? "#7f1d1d" : "#374151"}`,
          backgroundColor: "#111827",
          color: "white",
          fontSize: "14px",
          ...mono,
          boxSizing: "border-box",
        }}
      />

      {/* Candidates */}
      <div style={{ marginTop: "10px" }}>
        {candidates === undefined ? (
          <button
            type="button"
            onClick={loadCandidates}
            disabled={loadingCandidates}
            style={{ background: "none", border: "1px solid #374151", color: "#d1d5db", borderRadius: "6px", padding: "6px 10px", fontSize: "12px", cursor: "pointer" }}
          >
            {loadingCandidates ? "Loading nearby stations…" : "Show nearest stations"}
          </button>
        ) : candidates === null ? (
          <span style={{ color: "#fbbf24", fontSize: "12px" }}>Could not load nearby stations from the live-wind service.</span>
        ) : candidates.length === 0 ? (
          <span style={{ color: "#9ca3af", fontSize: "12px" }}>No stations near this spot.</span>
        ) : (
          <div style={{ ...box, display: "flex", flexDirection: "column", gap: "6px" }}>
            {candidates.map((s) => {
              const selected = s.id === value.trim();
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onChange(s.id)}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: "8px",
                    textAlign: "left",
                    background: selected ? "#1e3a5f" : "none",
                    border: "none",
                    color: "#d1d5db",
                    padding: "4px 6px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    cursor: "pointer",
                  }}
                >
                  <span>
                    <span style={{ color: "#f9fafb" }}>{s.name}</span>
                    <span style={{ color: "#6b7280" }}> · {s.distanceKm != null ? `${s.distanceKm.toFixed(1)} km` : ""} · {s.source}</span>
                  </span>
                  <span style={{ color: s.stale ? "#fbbf24" : "#34d399", whiteSpace: "nowrap" }}>{fmtReading(s)}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default LiveStationPicker;
